import { routePathNameChanged } from "@features/app/model";
import { StudentTipsApi } from "@features/shared/api/course-data.api";
import { IAiTip } from "@features/shared/entities/tips";
import {
    tipPromptCopied,
    tipViewed
} from "@features/shared/events";
import {
    ModelContract,
    ModuleDescription
} from "@features/shared/module-description";
import { IUserAuthContract } from "@features/student/auth/model";
import { createEffect, createEvent, createStore, sample } from "effector";
import { reset } from "patronum";
import { routerPush } from "@features/shared/router";
import { AnalyticsService } from "@features/shared/analytics";

const tipCopyButtonClicked = createEvent();
const tipTryButtonClicked = createEvent<{ prompt: string; model?: string }>();
const clearCurrentPrompt = createEvent()

const $currentTip = createStore<IAiTip | null>(null);
const $currentPrompt = createStore<string>("")



const getTipByIdFx = createEffect(async (tipId: number) => {
    const response = await StudentTipsApi.getTipById(tipId);

    return response;
});

const trackTipViewedFx = createEffect<
    { tipId: number; category: string },
    void
>(({ tipId, category }) => {
    tipViewed({ tipId, category });
});

const trackTipCopyClickedFx = createEffect<{ tipId: number }, void>(
    ({ tipId }) => {
        tipPromptCopied(tipId);
    }
);
const tipTryClicked = (payload: { tipId: number }) => {
    AnalyticsService.trackEvent("try_now_clicked", payload);
};

const trackTipTryClickedFx = createEffect(tipTryClicked);

sample({
    clock: getTipByIdFx.doneData,
    target: $currentTip
});

sample({
    clock: getTipByIdFx.doneData,
    source: {},
    fn: ({ }, payload) => ({
        tipId: payload.content.id,
        category: payload.content.category.caption
    }),
    target: trackTipViewedFx
});

sample({
    clock: tipCopyButtonClicked,
    source: $currentTip,
    filter: (tip): tip is IAiTip => tip !== null,
    fn: (tip) => ({ tipId: (tip as IAiTip).content.id }),
    target: trackTipCopyClickedFx
});

sample({
    clock: tipTryButtonClicked,
    source: $currentTip,
    filter: (tip): tip is IAiTip => tip !== null,
    fn: (tip) => ({ tipId: (tip as IAiTip).content.id }),
    target: trackTipTryClickedFx
});


sample({
    clock: tipTryButtonClicked,
    fn: ({ prompt }) => prompt,
    target: $currentPrompt,
})

sample({
    clock: clearCurrentPrompt,
    fn: () => "",
    target: $currentPrompt,
})

sample({
    clock: tipTryButtonClicked,
    fn: ({ model }) => {
        const query = new URLSearchParams();
        if (model) query.set("model", model);
        return `/assistant?${query.toString()}`;
    },
    target: routerPush,
});

const contract = new ModelContract(
    {
        $currentTip,
        $currentPrompt,
    },
    {
        tipCopyButtonClicked,
        tipTryButtonClicked,
        clearCurrentPrompt,
    },
    {},
    {}
);

export type ICurrentTipContract = typeof contract;

export class CurrentTipModule extends ModuleDescription<ICurrentTipContract> {
    constructor(public userAuthModule: IUserAuthContract) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run(): void {
        sample({
            clock: routePathNameChanged,
            filter: (url: string) => url.match(/\/ai-tips\/(\d+)/) !== null,
            fn: (url: string) => {
                const match = url.match(/\/ai-tips\/(\d+)/)!;
                return Number(match[1]);
            },
            target: getTipByIdFx
        });

        reset({
            clock: this.userAuthModule.events.logoutStudent,
            target: $currentTip
        });
    }
}
